import { useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { useCandidate } from '@/services/pre-enrollment';
import { StudentContext } from '@/contexts';
import {
  useAuthentication,
  useClient,
  useMounted,
  useStateMachine,
  useToken,
} from '@/hooks';
import { isExpiredTokenError } from '@/infrastructures/interceptors/handleExpiredTokenError';
import useLogout from './useLogout';

export default function GetClientData(): null {
  const { authenticated } = useAuthentication();
  const { getCandidateState } = useCandidate();
  const { loginIndentityData } = useToken();
  const client = useClient();
  const { state, setState } = useContext(StudentContext);
  const history = useHistory();
  const mounted = useMounted();
  const { logout } = useLogout();
  const { pathname } = useLocation();
  const { getStateObject } = useStateMachine();

  useEffect(() => {
    if (authenticated) {
      if (loginIndentityData?.data?.user?.roles?.includes('student')) {
        if (window.location.pathname === '/') history.push('/payment-history');
      } else {
        getCandidateState()
          .then(async (res) => {
            const { data } = await res;
            if (mounted()) {
              setState({
                ...state,
                paymentMethod:
                  data?.msc_pipeline?.msc_pipeline_process?.payment_method,
                hasFifteenDelayApproval:
                  data?.msc_pipeline?.pre_approved_payments_methods?.includes(
                    'fifteen_delay',
                  ) || false,
                mscPipelineId: data?.msc_pipeline?.id,
                selectionProcessId:
                  data?.msc_pipeline?.msc_pipeline_process.selection_process_id,
                processNameAbbreviation:
                  data?.msc_pipeline?.msc_pipeline_process
                    .process_name_abbreviation,
                clientId: data?.id,
                candidateApplicationId: data?.candidate_application_id,
                step: data?.msc_pipeline.actual_state,
                userId: data?.user_id,
                processDateInfo: {
                  limitDateAssignatureContract:
                    data?.msc_pipeline.msc_pipeline_process
                      .limit_date_assignature_contract,
                  limitDateFinancingRequest:
                    data?.msc_pipeline.msc_pipeline_process
                      .limit_date_financing_request,
                  processDueDate:
                    data?.msc_pipeline.msc_pipeline_process.process_due_date,
                },
                clientData: {
                  address: data?.address,
                  address_complement: data?.address_complement,
                  birthdate: data?.birthdate,
                  birthplace: data?.birthplace,
                  city: data?.city,
                  cpf: data?.cpf,
                  cpf_document_url: data?.cpf_document_url,
                  document_selfie_url: data?.document_selfie_url,
                  marital_status: data?.marital_status,
                  monthly_income: data?.monthly_income,
                  name: data?.name,
                  nationality: data?.nationality,
                  neighborhood: data?.neighborhood,
                  politically_exposed_person: data?.politically_exposed_person,
                  proof_of_residency_document_url:
                    data?.proof_of_residency_document_url,
                  residence_number: data?.residence_number,
                  residence_type: data?.residence_type,
                  rg: data?.rg,
                  rg_document_url: data?.rg_document_url,
                  rg_issuing_body: data?.rg_issuing_body,
                  rg_issuing_state: data?.rg_issuing_state,
                  state: data?.state,
                  student_financing_check: data?.student_financing_check,
                  zipcode: data?.zipcode,
                },
              });
              client.setClientId(data.id);
              client.setMscPipelineId(data?.msc_pipeline?.id);
              client.setSelectionProcessId(
                data?.msc_pipeline?.msc_pipeline_process.selection_process_id,
              );
              client.setActualState(data?.msc_pipeline.actual_state);
              client.setCandidateApplicationId(data?.candidate_application_id);
              client.setProcessDateInfo({
                limitDateAssignatureContract:
                  data?.msc_pipeline.msc_pipeline_process
                    .limit_date_assignature_contract,
                limitDateFinancingRequest:
                  data?.msc_pipeline.msc_pipeline_process
                    .limit_date_financing_request,
                processDueDate:
                  data?.msc_pipeline.msc_pipeline_process.process_due_date,
              });
              client.setProcessInfo({
                processName:
                  data?.msc_pipeline.msc_pipeline_process?.process_name,
                processNameAbbreviation:
                  data?.msc_pipeline.msc_pipeline_process
                    ?.process_name_abbreviation,
              });
              client.setStateMachineVersion(
                data?.msc_pipeline.msc_pipeline_process.pipeline_version,
              );

              const stateObj = getStateObject({
                state: data?.msc_pipeline.actual_state,
              });
              if (stateObj && stateObj.allowedUrls.indexOf(pathname) < 0) {
                history.push(stateObj.allowedUrls[0]);
              }
            }
          })
          .catch((err) => {
            if (isExpiredTokenError(err)) logout();
            if (err.response?.status === 401 || err.response?.status === 403) {
              const payload = {
                userId: client.clientId,
                currentState: client.currentState,
              };
              Sentry.setUser({ id: client.clientId });
              Sentry.captureException({ err, payload });
              history.push('/error');
            } else {
              const payload = {
                userId: client.clientId,
                currentState: client.currentState,
              };
              Sentry.setUser({ id: client.clientId });
              Sentry.captureException({ err, payload });
            }
            history.push('/error');
          });
      }
    } else {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
